import { EServerApplication } from '@/src/constants/constants'
import { isBitrixProjectMode, type ProjectMode } from '@/src/constants/projectModes'

interface IStartServerRequest {
  roomName: string
  application?: (typeof EServerApplication)[keyof typeof EServerApplication]
  project?: string | null
}

export const checkIsCorrectLivekitUrl = (livekit_url: string) =>
  livekit_url?.split('.')?.length >= 2

const dedicatedAgentRoomTypes = new Set<string>([
  'voiceAssistant',
  'voiceAssistant2',
  'textAssistant',
  EServerApplication.voiceAssistant,
  EServerApplication.voiceAssistant2,
  EServerApplication.textAssistant,
])

/** Общий LiveKit допустим только для комнат, которым не нужен выделенный агент. */
export const canUseSharedLivekitServer = (roomType: unknown): boolean =>
  typeof roomType !== 'string' || !dedicatedAgentRoomTypes.has(roomType)

const normalizeLivekitServerAddress = (livekitUrl: string): string =>
  livekitUrl
    .trim()
    .replace(/^(?:wss?|https?):\/\//i, '')
    .replace(/\/+$/, '')
    .toLowerCase()

/** Проверяет конкретный URL, чтобы не переиспользовать общий сервер из sticky-кеша. */
export const canRouteRoomToLivekitUrl = (
  roomType: unknown,
  livekitUrl: string,
  sharedLivekitUrl: string,
): boolean =>
  canUseSharedLivekitServer(roomType) ||
  normalizeLivekitServerAddress(livekitUrl) !==
    normalizeLivekitServerAddress(sharedLivekitUrl)

export const startMeetServer = async (
  roomName: string,
  roomType: keyof typeof EServerApplication | null,
  project: string | null,
) => {
  let livekit_url: string | null = null
  let status: number | null = null

  let body: IStartServerRequest = { roomName }

  if (roomType) {
    body = { ...body, application: EServerApplication[roomType] }
  }

  if (project) {
    body = { ...body, project }
  }

  try {
    const response = await fetch(`${process.env.NEXT_PUBLIC_MEET_API}/start_server`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: process.env.ORCHESTRATOR_TOKEN,
      },
      body: JSON.stringify(body),
    })

    status = response.status

    if (!response.ok) {
      throw new Error(`Error during request for starting meet server. Status: ${response.status}`)
    }

    let serverResponse
    try {
      serverResponse = await response.json()
      console.log('[startMeetServer] orchestrator response', {
        roomName,
        roomType,
        application: body.application ?? null,
        status,
        ipAddress: serverResponse?.ip_address ?? null,
      })
    } catch (e) {
      throw new Error(
        `Error during parsing response to json after starting meet server request. Error: ${e.message}`,
      )
    }

    const isCorrectLivekitUrl =
      response.status === 200 && checkIsCorrectLivekitUrl(serverResponse?.ip_address)

    if (isCorrectLivekitUrl) {
      livekit_url = `wss://${serverResponse.ip_address}`
    } else if (canUseSharedLivekitServer(roomType)) {
      livekit_url = `wss://${process.env.NEXT_PUBLIC_LIVEKIT_URL}`
    } else {
      status = 503
    }

    return { livekit_url, status }
  } catch (error) {
    if (error instanceof Error) {
      console.error('[startMeetServer] orchestrator request failed', {
        roomName,
        roomType,
        application: body.application ?? null,
        status,
        error: error.message,
      })
    } else {
      console.error('[startMeetServer] unknown orchestrator error', {
        roomName,
        roomType,
        application: body.application ?? null,
        status,
        error,
      })
    }

    if (!status) {
      status = 500
    }

    if (!livekit_url && canUseSharedLivekitServer(roomType)) {
      livekit_url = `wss://${process.env.NEXT_PUBLIC_LIVEKIT_URL}`
    }
  }

  return { livekit_url, status }
}

export const createMeetToken = async (roomName: string, origin: string) => {
  if (!roomName || !origin) {
    if (!roomName) {
      console.error(`Has not roomName into createMeetToken function`)
    }

    if (!origin) {
      console.error(`Has not origin into createMeetToken function`)
    }

    return
  }

  try {
    const customOrigin = origin

    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 3000)

    const response = await fetch(`${customOrigin}/api/create_meet_token`, {
      method: 'POST',
      body: JSON.stringify({
        roomName,
        origin: customOrigin,
      }),
      signal: controller.signal,
    }).finally(() => clearTimeout(timeoutId))

    const result = await response?.json().catch((err) => {
      console.error(
        `Error with JSON response from create_meet_token: ${err?.message || 'Unknown error'}`,
      )
      throw new Error(
        `Error with JSON response from create_meet_token: ${err?.message || 'Unknown error'}`,
      )
    })

    return result as { meet_id: string; meet_token: string }
  } catch (e) {
    console.error(
      `Error with creating meet token. roomName is ${roomName}, origin is ${origin}. ${
        e?.message || "Couldn't find the error"
      }`,
    )
  }
}

export const getRoomType = (
  v: string | string[] | null,
  interview?: boolean,
): keyof typeof EServerApplication | null => {
  const isSpecial = (query: string | string[] | boolean | null, value?: string) =>
    typeof query === 'string' && (value ? query === value : query === '' || query === 'true')

  if (interview) {
    return 'voiceAssistant'
  }

  if (isSpecial(v, 'fa')) {
    return 'voiceAssistant'
  }

  if (isSpecial(v, 'ma')) {
    return 'voiceAssistant'
  }

  if (isSpecial(v, 'sip')) {
    return 'sip'
  }

  if (isSpecial(v, 'hr')) {
    return 'textAssistant'
  }

  if (isSpecial(v, 'm')) {
    return 'meet'
  }

  return null
}

/**
 * Определяет тип комнаты (roomType) для cookie, Redux и запроса /api/start_server.
 * 
 * ---
 *
 * Обычно, тип комнаты берётся из query-параметра `v` (см. getRoomType: fa → голосовой агент,
 * hr → текстовый, m → meet и т.д.). Если `v` не задан, roomType остаётся null, и meet
 * не передаёт orchestrator поле application — тогда оркестратор записывает звонок с
 * current_application: null.
 *
 * В dev и stage окружении в коде агента (meet-agent-nodejs) при `current_application: null`
 * подставляется AGENT_TYPE из env контейнера, хотя в этом случае подразумевается mainAgent.
 * Из-за этого, например, в звонки с интеграции Битрикса может подключиться голосовой агент Федор
 * или текстовый агент для подсказок рекрутёру в собеседовании, а не обычный агент, который
 * будет работать с транскрипцией.
 * 
 * prod в обычных звонках защищён от этой проблемы: в этом окружении крутится отдельная ветка
 * meet-agent-nodejs — `main-common-agent`. В этой ветке нет Фёдора и текстовых подсказок,
 * а ещё она не работает с `current_application` из оркестратора.
 *
 * Bitrix-звонки (те, что с параметром `?project=bitrix` в Url) никогда не несут `v` в URL:
 * это обычные видеовстречи с транскрипцией, как на prod. Поэтому для project=bitrix без явного `v`
 * принудительно выставляем roomType = 'meet'. Meet отправит в оркестратор `application: meet`,
 * оркестратор запишет звонок с `current_application: meet` и запустит meet-agent-nodejs
 * именно с агентом из mainAgent.ts.
 *
 * Явный параметр `v` в URL, а также флаг `interview`, по-прежнему имеют приоритет.
 */
export const resolveRoomType = ({
  v,
  interview,
  projectMode,
}: {
  v: string | string[] | null | undefined
  interview?: boolean
  projectMode: ProjectMode | null
}): keyof typeof EServerApplication | null => {
  const roomTypeFromQuery = getRoomType(v ?? undefined, interview)

  if (roomTypeFromQuery) {
    return roomTypeFromQuery
  }

  if (isBitrixProjectMode(projectMode)) {
    return 'meet'
  }

  return null
}

export const getRoomName = (
  roomName: string,
  roomNameFromQuery?: string | string[],
  isIsolate?: boolean,
) => {
  if (roomNameFromQuery) {
    return `${roomName}-${roomNameFromQuery}`
  }

  if (isIsolate) {
    return `${roomName}-${Math.random()?.toString()?.split('.')?.at(1)}`
  }

  return roomName
}

export const convertLivekitUrlToWss = (livekit_url: string) => {
  let customLivekitUrl: string = ''

  if (livekit_url?.includes('https://')) {
    customLivekitUrl = livekit_url?.replace('https://', 'wss://')
  } else if (!livekit_url?.includes('wss://')) {
    customLivekitUrl = `'wss://'${livekit_url}`
  } else {
    customLivekitUrl = livekit_url
  }

  return customLivekitUrl
}
